import React, { useState, useMemo, useEffect } from 'react';
import { useIntl, getLocale } from 'umi';
import { Justify, Button } from 'tea-component';
import { PER_TYPES, REVENUE_TYPES, GENRE_TYPES, REGION_MARKET_TYPES, TABLE_SEARCH_PLACEHOLDERS } from '../../constants';
import SearchRecent from './SearchRecent';
import DownloadModal from './DownloadModal';
import useRefreshRecentSearchStateFlag from './hooks/useRefreshRecentSearchStateFlag';
import type { IListDataItem } from '../../types';

export default function SearchAndDownloads(props: any) {
  const {
    isMobile,
    isDodHide,
    filter,
    regionOpts,
    marketOpts,
    backfillingAfterSearch,
    total,
    perType,
    revenueType,
    metrics,
    tableAction,
    getPageParams,
    searchApi,
    getIcon,
    renderTableData,
    pageLogInfo,
    setTableLoading,
    isChartLoading,
    setChartLoading,
    updateFlagOfAutoCompleteOptions,
    onAutoSearchSelect,
    genreOpts,
  } = props;

  // console.log('SearchAndDownloads',props);

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const [isOpen, setIsOpen] = useState<any>(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  // 搜索组件内 监听了这个值，这个值change 就会清空，并且重新获取对应的搜索记录 当前输入的关键字，这个值在 1、2切换
  const initSearchInputNum = useRefreshRecentSearchStateFlag({
    perType,
    revenueType,
    filter,
  });

  const searchType = useMemo(() => {
    const prefix = `per${perType}-${revenueType}`;
    if (perType === PER_TYPES.MARKET) return `${prefix}-${filter.regionType}`;
    if (revenueType !== REVENUE_TYPES.MOBILE) return prefix;
    if (filter.genreType !== GENRE_TYPES.MAIN_SUB) return `${prefix}-${filter.genreType}`;
    return `${prefix}-mainSub`;
  }, [perType, revenueType, filter.regionType, filter.genreType]);

  const transTableDataForSearch = (res: any) => {
    const getMobileresult = (_result = []) => {
      return _result.find((item: any) => item?.metric === metrics) || [];
    }
    const { list = [], chart_list = [], pie_list = [] } = (isMobile ? getMobileresult(res?.data) : res?.data) || {};
    // 全量返回，因为需要用到可视化数据
    return {
      chartList: chart_list || [],
      pieList: pie_list || [],
      list: list.map((item: IListDataItem) => ({
        ...item,
        rank: 0,
        icon: getIcon(item)
      })) || []
    };
  };

  const transRecordDataForSearch = (res: any) => {
    const recordList = res?.data || [];
    if (!Array.isArray(recordList) || !recordList?.length) {
      return [];
    }
    const tranksList: IListDataItem[] = recordList.map(item => {
      const { metric_name, metric_name_cn, country_abbre } = item;
      const _obj = {
        name: metric_name,
        name_abbr: country_abbre,
        name_cn: metric_name_cn,
        total: 0,
        metrics: []
      }
      return {
        ..._obj,
        // 点击后，表格只展示一条数据，所以序号永远是0
        rank: 0,
        icon: getIcon(_obj)
      };
    });

    return tranksList;
  };

  // 调用设置表格的方法，将搜索结果展示在前端
  const setTableSearchResult = (result: any) => {
    // // TODO 搜索后，可视化是否勾选第一条
    // renderTableData([item], 1, true);

    // 兼容搜索为空的情况,item会是undefined
    // const searchResults = item ? [item] : [];
    // console.log(searchResults);
    renderTableData(result);
    if (typeof onAutoSearchSelect === 'function') {
      onAutoSearchSelect(result);
    }
  }

  const resetTableData = () => {
    backfillingAfterSearch(true, true);
  };

  const getAddRecordParams = (item) => {
    const { name, name_cn, name_abbr } = item;
    return {
      metric_name: name,
      metric_name_cn: name_cn,
      country_abbre: name_abbr,
    };
  };

  const getDeleteRecordParams = (item) => {
    const { name } = item;
    return {
      name: name,
    };
  }

  const tablePlaceholder = useMemo(() => {
    let type = 'region';
    if (perType === PER_TYPES.GENRE) {
      type = 'genre';
    } else if (filter.regionType === REGION_MARKET_TYPES.MARKET) {
      type = 'market';
    }
    // return `Search any ${type} in the table`

    const searchPlaceholderLocaleKey = TABLE_SEARCH_PLACEHOLDERS[type] ?? 'dbi_label_search';
    return formatMessage({ id: searchPlaceholderLocaleKey });
  }, [locale, filter.regionType, perType]);

  // 监听search的变化，从而影响表格和可视化的loading
  useEffect(() => {
    setTableLoading(isSearchLoading);
    if (filter.regionType === REGION_MARKET_TYPES.REGION) {
      if (!isSearchLoading && isMobile && isChartLoading) {
        return;
      }
      setChartLoading(isSearchLoading);
    }
  }, [isSearchLoading]);

  // genre榜单,是否是默认全选Genre
  const isNormalAllGenre = useMemo(() => {
    if (isMobile && filter?.genreType !== 'main') {
      return filter?.mainGenres?.length === 0 && filter?.subGenres?.length === 0;
    } else {
      return genreOpts?.length === filter?.genres?.length;
    }
  }, [genreOpts, filter?.genres, filter?.mainGenres, filter?.subGenres, filter?.genreType, isMobile]);

  return (
    <div className='global-view-search-bar'>
      <Justify
        className="card-justify"
        right={
          <>
            <SearchRecent
              key={searchType}
              searchKey='search'
              searchType={searchType}
              searchLoading={isSearchLoading}
              setSearchLoading={setIsSearchLoading}
              initSearchInput={initSearchInputNum}
              fetchParams={getPageParams}
              searchListApi={searchApi}
              pageLogInfo={pageLogInfo}
              transTableDataForSearch={transTableDataForSearch}
              transRecordDataForSearch={transRecordDataForSearch}
              getTableResultList={setTableSearchResult}
              handleClear={resetTableData}
              placeholder={tablePlaceholder}
              isCustomRendering={true}
              resetSearchType={`${searchType}_${filter?.regionType}_${filter?.genreType}_${metrics}`}
              getAddRecordParams={getAddRecordParams}
              getDeleteRecordParams={getDeleteRecordParams}
              updateFlagOfAutoCompleteOptions={updateFlagOfAutoCompleteOptions}
            />
            <Button
              icon="download"
              className="ns-btn--icon"
              title="下载"
              onClick={() => {
                setIsOpen(true);
              }}
            />
          </>
        }
      />
      <DownloadModal
        key={searchType}
        isMobile={isMobile}
        isDodHide={isDodHide}
        perType={perType}
        revenueType={revenueType}
        metrics={metrics}
        filter={filter}
        regionOpts={regionOpts}
        marketOpts={marketOpts}
        tableAction={tableAction}
        total={total}
        isOpen={isOpen}
        isNormalAllGenre={isNormalAllGenre}
        onClose={() => {
          setIsOpen(false);
        }}
      />
    </div>
  )
}
